<template>
  <!-- Simple/Carousel mode -->
  <div v-if="isCarouselUrl" class="product-container">
    <p class="notification-subtitle paddingBottom no-margin">URL type:</p>
    <VSelect
      v-model="localUrlType"
      aria-label="URL type"
      appearance="ghost"
      class="vivid-input customMarginBottom"
      fixed-dropdown
      @change="emitChange"
    >
      <VOption value="static" text="Static" />
      <VOption v-if="showDynamicURL" value="dynamic" text="Dynamic" />
    </VSelect>
    <div v-if="localUrlType === 'dynamic'" class="info-box marginBottom">
      <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
      <p class="info-text no-margin">Dynamic URL allows you to create a URL with 1 variable (&#123;&#123;1&#125;&#125;) which can be at the end of the URL. For e.g.: https://jumper.ai/order-receipt/&#123;&#123;1&#125;&#125;</p>
    </div>
    <p class="notification-subtitle paddingBottom no-margin">Enter a URL</p>
    <VTextField v-model="localUrl" type="url" :maxlength="2000" aria-label="website" placeholder="Enter URL" appearance="ghost" class="vivid-input" @blur="emitChange"></VTextField>
  </div>

  <!-- Detailed mode (bot builder, broadcast, template) -->
  <div v-else>
    <div class="reply-container">
      <div class="flex-start paddingBottom flexText">
        <p class="notification-subtitle no-margin">Enter a URL</p>
        <HelpTooltip v-if="localUrlType === 'dynamic'">
          {{ `Enter a custom field or a combination of a URL and custom field (e.g., <custom_field_url> or https://jumper.ai/<custom_field_url>), where the custom field contains either the full URL or a portion of the URL. The custom field can come from either a CSV file or the CRM.` }}
        </HelpTooltip>
      </div>
      <div style="display: flex; flex-wrap: wrap; gap: 10px;">
        <VSelect
          v-model="localUrlType"
          aria-label="URL type"
          appearance="ghost"
          class="vivid-input"
          fixed-dropdown
          style="width: 18%; min-width: 140px; flex: 1 0;"
          @change="emitChange"
        >
          <VOption value="static" text="Static URL" />
          <VOption value="dynamic" text="Dynamic URL" />
        </VSelect>
        <VTextField
          v-if="localUrlType === 'static'"
          v-model="localUrl"
          type="url"
          :maxlength="2000"
          placeholder="Enter URL"
          aria-label="Enter URL"
          appearance="ghost"
          class="vivid-input"
          style="height: 40px; min-width: 250px; flex: 1 0;"
          @blur="emitChange"
        ></VTextField>
        <custom-field-input
          v-else
          v-model="localUrl"
          placeholder="Enter URL here..."
          style="height: 40px; min-width: 250px; flex: 1 0;"
          @update:modelValue="emitChange"
        />
      </div>
    </div>

    <div class="reply-container">
      <div class="flex-start paddingBottom flexText">
        <p class="notification-subtitle no-margin">Open in</p>
        <HelpTooltip>
          {{ `WebView opens the page inside the messaging app when supported.` }}
        </HelpTooltip>
      </div>
      <div style="display: flex; flex-wrap: wrap; gap: 10px;">
        <VSelect
          v-model="localOpenurlType"
          aria-label="Open in"
          appearance="ghost"
          class="vivid-input"
          fixed-dropdown
          style="width: 18%; min-width: 140px; flex: 1 0;"
          @change="emitChange"
        >
          <VOption value="browser" text="Browser (Default)" />
          <VOption value="webview" text="WebView" />
        </VSelect>
      </div>
    </div>

    <div v-if="localOpenurlType === 'webview'" class="reply-container">
      <div class="flex-start paddingBottom flexText">
        <p class="notification-subtitle no-margin">Display mode</p>
        <HelpTooltip>
          {{ `Controls how much of the screen the web content occupies.` }}
        </HelpTooltip>
      </div>
      <div :class="['display-mode-radio-group', { 'display-mode-radio-group--inline': isBotbuilder }]">
        <label
          v-for="option in displayModeOptions"
          :key="option.value"
          class="display-mode-radio-wrapper"
          @mouseenter="hoveredDisplayMode = option.value"
          @mouseleave="hoveredDisplayMode = null"
          tabindex="0"
          @focus="hoveredDisplayMode = option.value"
          @blur="hoveredDisplayMode = null"
        >
          <input type="radio" :value="option.value" v-model="localDisplayMode" class="display-mode-radio-native" @change="emitChange" />
          <div class="display-mode-radio-item" :class="{ 'display-mode-radio-item--selected': localDisplayMode === option.value }">
            <span class="title">{{ option.label }}</span>
          </div>
          <transition name="fade">
            <div v-if="hoveredDisplayMode === option.value" class="display-mode-hover-preview">
              <div class="display-mode-preview-image-area">
                <img :src="option.image" :alt="option.label" class="display-mode-preview-img" />
              </div>
              <div class="display-mode-preview-text-area">
                <p class="display-mode-preview-desc">{{ option.description }}</p>
              </div>
            </div>
          </transition>
        </label>
      </div>
    </div>

    <div v-if="localOpenurlType === 'webview'" class="reply-container">
      <div class="paddingBottom">
        <p class="notification-subtitle no-margin">Accessibility Description (Optional)</p>
      </div>
      <div class="counter-wrapper">
        <VTextArea
          v-model="localAccessibilityDescription"
          :rows="5"
          appearance="ghost"
          class="vivid-textarea"
          aria-label="accessibility description"
          placeholder="Opens checkout page in an in-app webview."
          :maxlength="accessibilityDescriptionLimit"
          @blur="emitChange"
        ></VTextArea>
        <div class="custom-counter">{{ accessibilityDescriptionLength }}/{{ accessibilityDescriptionLimit }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import HelpTooltip from '@/components/HelpTooltip.vue';
import CustomFieldInput from '@/components/CustomFieldInput.vue';
import fullWebviewPng from '@/resources/images/full_webview.png';
import tallWebviewPng from '@/resources/images/tall_webview.png';
import halfWebviewPng from '@/resources/images/half_webview.png';

export interface WebsitePayload {
  url: string
  urlType: 'static' | 'dynamic'
  openurlType: 'browser' | 'webview'
  displayMode: string
  accessibilityDescription: string
}

const props = defineProps<{
  modelValue: WebsitePayload
  showDynamicURL?: boolean
  isBotbuilder?: boolean
  isCarouselUrl?: boolean
}>();

const emit = defineEmits<{
  'update:modelValue': [value: WebsitePayload]
}>();

const localUrl = ref(props.modelValue.url);
const localUrlType = ref<'static' | 'dynamic'>(props.modelValue.urlType);
const localOpenurlType = ref<'browser' | 'webview'>(props.modelValue.openurlType);
const localDisplayMode = ref(props.modelValue.displayMode);
const localAccessibilityDescription = ref(props.modelValue.accessibilityDescription);
const hoveredDisplayMode = ref<string | null>(null);

const accessibilityDescriptionLimit = 500;
const accessibilityDescriptionLength = computed(() => (localAccessibilityDescription.value || '').length);

watch(() => props.modelValue, (val) => {
  localUrl.value = val.url;
  localUrlType.value = val.urlType;
  localOpenurlType.value = val.openurlType;
  localDisplayMode.value = val.displayMode;
  localAccessibilityDescription.value = val.accessibilityDescription;
}, { deep: true });

const displayModeOptions = [
  {
    value: 'full',
    label: 'Full (100%)',
    description: 'The Full web page takes up 100% of the screen.',
    image: fullWebviewPng,
  },
  {
    value: 'tall',
    label: 'Tall (75%)',
    description: 'The Tall web page takes up 75% of the screen.',
    image: tallWebviewPng,
  },
  {
    value: 'half',
    label: 'Half (50%)',
    description: 'The Half web page takes up 50% of the screen.',
    image: halfWebviewPng,
  },
];

function emitChange() {
  emit('update:modelValue', {
    url: localUrl.value,
    urlType: localUrlType.value,
    openurlType: localOpenurlType.value,
    displayMode: localDisplayMode.value,
    accessibilityDescription: localAccessibilityDescription.value,
  });
}
</script>

<style scoped>
.product-container {
  margin-top: 20px;
  margin-bottom: 20px;
}
.reply-container {
  padding-bottom: 10px;
  margin-bottom: 10px;
}
.customMarginBottom {
  margin-bottom: 20px;
}
.paddingBottom {
  padding-bottom: 10px;
}
.no-margin {
  margin: 0;
}
.display-mode-radio-group {
  display: flex !important;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
}
.display-mode-radio-group--inline {
  flex-wrap: nowrap;
  flex-direction: column;
}
.display-mode-radio-wrapper {
  position: relative;
  flex: 1;
  min-width: 90px;
  cursor: pointer;
}
.display-mode-radio-native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.display-mode-radio-item {
  width: 100%;
  background: var(--background-primary);
  border-radius: 4px;
  padding: 8px 12px;
  transition: border-color 0.2s;
  border: 2px solid transparent;
  text-align: center;
}
.display-mode-radio-item--selected {
  border-color: var(--purple);
  background: var(--purple-opac);
}
.display-mode-hover-preview {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: transparent;
  border-radius: 10px;
  overflow: hidden;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  width: 210px;
  text-align: center;
}
.display-mode-preview-image-area {
  background: #f0f0f0;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.display-mode-preview-img {
  width: 170px;
  height: 170px;
  object-fit: contain;
  display: block;
}
.display-mode-preview-text-area {
  background: var(--white, #ffffff);
  padding: 10px 14px 12px;
}
.display-mode-preview-desc {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: var(--black, #1a1a1a);
  line-height: 1.4;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.info-box {
  display: flex;
  align-items: center;
  flex-direction: row;
  background: var(--purple-opac);
  padding: 15px 20px;
  border-radius: 5px;
}
.info-text {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin-left: 8px;
}
.marginBottom {
  margin-bottom: 20px;
}
</style>
